import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './SkinTypeCarousel.css';

const SKIN_TYPES = ['Normal', 'Dry', 'Oily', 'Combination', 'Sensitive'];

const getSkinIconName = (label) => {
    const s = (label || '').toLowerCase();
    if (s.includes('normal')) return 'natural';
    if (s.includes('dry')) return 'dry';
    if (s.includes('oily')) return 'oily';
    if (s.includes('combination')) return 'combination';
    if (s.includes('sensitive')) return 'sensitive';
    return (label || '').split(/\s+/)[0].toLowerCase() || 'natural';
};

const SkinTypeCarousel = ({ selectedType, onSelect, options: optionsProp }) => {
    const skinTypes = useMemo(() => (Array.isArray(optionsProp) && optionsProp.length > 0 ? optionsProp : SKIN_TYPES), [optionsProp]);

    // Clone logic: script.js clones first and last
    const extendedItems = useMemo(() => [
        skinTypes[skinTypes.length - 1], // Last clone at start
        ...skinTypes,
        skinTypes[0] // First clone at end
    ], [skinTypes]);

    const [index, setIndex] = useState(1);
    const trackRef = useRef(null);
    const containerRef = useRef(null);

    // State for dynamic measurements
    const [measurements, setMeasurements] = useState({ itemWidth: 200, offset: 100 });

    // Helper: Measure dimensions from DOM
    const measureDimensions = useCallback(() => {
        if (containerRef.current && trackRef.current && trackRef.current.children.length > 0) {
            // Assume all items are same size, measure the first one styling
            // We need to account for margin if it exists. 
            // In CSS we added margin: 0 10px. So total width = width + 20.
            // Let's measure offsetWidth of item and add calculated margin

            const firstItem = trackRef.current.children[0];
            const itemW = firstItem.offsetWidth;
            const style = window.getComputedStyle(firstItem);
            const margin = parseFloat(style.marginLeft) + parseFloat(style.marginRight);
            const totalItemWidth = itemW + margin;

            const containerW = containerRef.current.offsetWidth;
            const newOffset = (containerW / 2) - (totalItemWidth / 2);

            setMeasurements({ itemWidth: totalItemWidth, offset: newOffset });
        }
    }, []);

    // Helper: getTransform(i) using dynamic state
    const getTransform = useCallback((i) => {
        return `translateX(-${i * measurements.itemWidth - measurements.offset}px)`;
    }, [measurements]);

    // Initialize position and resize listener
    useEffect(() => {
        measureDimensions();

        const handleResize = () => {
            measureDimensions();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [measureDimensions]);

    // Apply transform when measurements change or index changes
    useEffect(() => {
        if (trackRef.current) {
            // Disable transition for resize adjustments usually, but for simplicity just apply
            // If just resizing, we might want to disable transition
            trackRef.current.style.transform = getTransform(index);
        }
    }, [index, measurements, getTransform]);

    // Sync external selection if needed
    useEffect(() => {
        if (selectedType) {
            const typeIndex = skinTypes.indexOf(selectedType);
            if (typeIndex !== -1) {
                const carouselIndex = typeIndex + 1;

                // CRITICAL FIX: If current displayed item (potentially a clone) matches the selected type,
                // do NOT force a scroll. This allows the carousel to sit at the 'clone' position 
                // until the transitionEnd event resets it seamlessly.
                if (extendedItems[index] === selectedType) {
                    return;
                }

                if (index !== carouselIndex) {
                    setIndex(carouselIndex);
                    if (trackRef.current) {
                        trackRef.current.style.transition = "transform 0.4s ease";
                        trackRef.current.style.transform = getTransform(carouselIndex);
                    }
                }
            }
        }
    }, [selectedType, skinTypes, getTransform, index, extendedItems]); // Added index/extendedItems deps

    // next()
    const next = () => {
        if (index >= extendedItems.length - 1) return;
        const newIndex = index + 1;
        setIndex(newIndex);
        if (trackRef.current) {
            trackRef.current.style.transition = "transform 0.4s ease";
            trackRef.current.style.transform = getTransform(newIndex);
        }
        updateSelection(newIndex);
    };

    // prev()
    const prev = () => {
        if (index <= 0) return;
        const newIndex = index - 1;
        setIndex(newIndex);
        if (trackRef.current) {
            trackRef.current.style.transition = "transform 0.4s ease";
            trackRef.current.style.transform = getTransform(newIndex);
        }
        updateSelection(newIndex);
    };

    // updateSelection() wrapper to call onSelect
    const updateSelection = (idx) => {
        // Map carousel index to type
        // index 0 -> Last Item (clone)
        // index 1..N -> Real Items
        // index N+1 -> First Item (clone)

        let typeIndex = idx - 1;
        if (idx === 0) typeIndex = skinTypes.length - 1;
        if (idx === extendedItems.length - 1) typeIndex = 0;

        const type = skinTypes[typeIndex];
        if (type) onSelect(type);
    };

    // transitionend listener logic
    const handleTransitionEnd = (e) => {
        // Relaxed check: allow any transform property or just ensure it's the track
        if (!trackRef.current || e.target !== trackRef.current) return;
        // if (e.propertyName !== 'transform') return; // sometimes this can be 'all' or prefixed

        let newIndex = index;
        let didReset = false;

        // If at last clone (index N+1), jump to first real item (index 1)
        if (index === extendedItems.length - 1) {
            newIndex = 1;
            didReset = true;
        }
        // If at first clone (index 0), jump to last real item (index N)
        else if (index === 0) {
            newIndex = extendedItems.length - 2;
            didReset = true;
        }

        if (didReset) {
            trackRef.current.style.transition = "none";
            // Force reflow
            void trackRef.current.offsetHeight;
            trackRef.current.style.transform = getTransform(newIndex);

            // Manually update classes to prevent flicker before React render
            if (trackRef.current && trackRef.current.children) {
                Array.from(trackRef.current.children).forEach((child, i) => {
                    if (i === newIndex) {
                        child.classList.add('selected');
                    } else {
                        child.classList.remove('selected');
                    }
                });
            }

            setIndex(newIndex);
        }
    };

    // Self-healing: If stuck at clone index (0 or N+1) for too long (missed transitionEnd), force reset
    useEffect(() => {
        let timer;
        if (index === 0 || index === extendedItems.length - 1) {
            timer = setTimeout(() => {
                // If we are still here, force reset
                // Re-use logic:
                let newIndex = index;
                if (index === 0) newIndex = extendedItems.length - 2;
                if (index === extendedItems.length - 1) newIndex = 1;

                if (trackRef.current) {
                    trackRef.current.style.transition = "none";
                    void trackRef.current.offsetHeight;
                    trackRef.current.style.transform = getTransform(newIndex);
                }
                setIndex(newIndex);
            }, 500); // 400ms transition + buffer
        }
        return () => clearTimeout(timer);
    }, [index, extendedItems.length, getTransform]);

    // Drag Logic
    const startXRef = useRef(0);
    const baseOffsetRef = useRef(0);
    const isDraggingRef = useRef(false);
    const justDraggedRef = useRef(false);

    const getBaseOffset = (currentIdx) => {
        return currentIdx * measurements.itemWidth - measurements.offset;
    };

    const handleDragStart = (clientX) => {
        startXRef.current = clientX;
        baseOffsetRef.current = getBaseOffset(index);
        isDraggingRef.current = true;
        if (trackRef.current) trackRef.current.style.transition = "none";
    };

    const handleDragMove = (clientX) => {
        if (!isDraggingRef.current || !trackRef.current) return;
        const deltaX = clientX - startXRef.current;
        const newOffset = baseOffsetRef.current - deltaX;
        trackRef.current.style.transform = `translateX(-${newOffset}px)`;
    };

    const handleDragEnd = (clientX) => {
        if (!isDraggingRef.current || !trackRef.current) return;
        isDraggingRef.current = false;
        trackRef.current.style.transition = "transform 0.4s ease";

        const deltaX = clientX - startXRef.current;
        const threshold = measurements.itemWidth * 0.25;

        if (Math.abs(deltaX) > 5) justDraggedRef.current = true;

        if (deltaX > threshold) {
            prev();
        } else if (deltaX < -threshold) {
            next();
        } else {
            trackRef.current.style.transform = getTransform(index);
        }

        if (justDraggedRef.current) {
            setTimeout(() => { justDraggedRef.current = false; }, 150);
        }
    };

    // Setup global event listeners for drag to match script.js behavior (using window/document)
    // We use refs for handlers to access current state/refs without re-binding
    // For React, we'll attach mouse events to window/document via effect, and touch to component

    // Actually script.js attaches mouse move/up to DOCUMENT.
    useEffect(() => {
        const onMouseMove = (e) => {
            if (isDraggingRef.current) handleDragMove(e.clientX);
        };
        const onMouseUp = (e) => {
            if (isDraggingRef.current) handleDragEnd(e.clientX);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }); // No dependency array to ensure closures have latest scope if needed? Actually refs solve this.

    return (
        <div className="skin-carousel-container">
            <button className="skin-carousel-nav-btn" onClick={prev}>‹</button>

            <div
                className="skin-carousel-window"
                ref={containerRef}
                onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
                onTouchMove={(e) => {
                    if (isDraggingRef.current) e.preventDefault();
                    handleDragMove(e.touches[0].clientX);
                }}
                onTouchEnd={(e) => handleDragEnd(e.changedTouches[0].clientX)}
                onTouchCancel={() => {
                    if (isDraggingRef.current) {
                        isDraggingRef.current = false;
                        if (trackRef.current) {
                            trackRef.current.style.transition = "transform 0.4s ease";
                            trackRef.current.style.transform = getTransform(index);
                        }
                    }
                }}
                onMouseDown={(e) => {
                    e.preventDefault();
                    handleDragStart(e.clientX);
                }}
                onClick={(e) => {
                    if (justDraggedRef.current) {
                        e.preventDefault();
                        e.stopPropagation();
                        justDraggedRef.current = false;
                    }
                }}
            >
                <div
                    className="skin-carousel-track"
                    id="track"
                    ref={trackRef}
                    onTransitionEnd={handleTransitionEnd}
                >
                    {extendedItems.map((type, i) => {
                        const isSelected = selectedType === type;
                        const imageName = getSkinIconName(type);

                        return (
                            <div
                                key={`${type}-${i}`}
                                className={`skin-carousel-item ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                    if (justDraggedRef.current) return;

                                    if (i !== index) {
                                        if (i > index) next();
                                        else if (i < index) prev();
                                    } else {
                                        onSelect(type);
                                    }
                                }}
                            >
                                <div
                                    className="skin-carousel-item-image"
                                    style={{ '--skin-icon-mask': `url(${process.env.PUBLIC_URL}/skinicon/${imageName}.png)` }}
                                >
                                    <img src={`${process.env.PUBLIC_URL}/skinicon/${imageName}.png`} alt={type} />
                                </div>
                                <span>{type}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <button className="skin-carousel-nav-btn" onClick={next}>›</button>
        </div>
    );
};

export default SkinTypeCarousel;
